import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row } from 'reactstrap';
import {
    Button,
    DateInput,
    Icon,
    InputRadio,
    JustifyContents,
    Label,
    LabelPosition,
    NumericInput,
    TextArea,
    TextInput,
    ToogleSwitch,
} from 'tacho-ui';
import config from '../../../config';
import { TachoReleaseInformationModel } from '../../../core/domainModels/versions/TachoInstallerModel';
import { TachoReleaseType } from '../../../core/domainModels/versions/TachoReleaseType';
import {
    defaultUpdateTachoInstallerModel,
    UpdateTachoInstallerModel,
} from '../../../core/domainModels/versions/UpdateTachoInstallerModel';
import { createDateFromTimeZone, dateToStringFormat } from '../../../core/helpers/moment';
import { ApplicationState } from '../../../state/ApplicationState';
import { DispatchType } from '../../../state/configureStore';
import { getAllInstallersThunk, updateInstallerThunk } from '../../../state/versions/reducer';
import { TachoModal } from '../../components/TachoModal';
import { TachoEditModalTitle } from '../../components/TachoModal/TachoEditTitle';
import { execExtension, zippedExtension } from '../../pages/versions/models/versionsExtensions';
import { mapTachoInstallerToAddTachoInstaller } from '../../pages/versions/services/versionsService';
import './styles.scss';
import { isSHA256Valid } from '../../../core/helpers/sha256';
import { isMD5Valid } from '../../../core/helpers/md5';

interface UpdateVersionModalProps {
    installer: TachoReleaseInformationModel;
    isOpen: boolean;
    onToggle: () => void;
}
export const UpdateVersionModal = (props: UpdateVersionModalProps) => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch<DispatchType>();
    const [installerData, setInstallerData] = useState<UpdateTachoInstallerModel>(
        props.installer ? mapTachoInstallerToAddTachoInstaller(props.installer) : defaultUpdateTachoInstallerModel,
    );

    const onSubmit = () => {
        dispatch(updateInstallerThunk(installerData))
            .then(success => {
                if (success) {
                    props.onToggle();
                    dispatch(getAllInstallersThunk());
                }
            })
            .catch(err => console.log(err));
    };
    const updateInstallerPending = useSelector((state: ApplicationState) => state.versions.updateInstallerPending);
    const updateInstallerError = useSelector((state: ApplicationState) => state.versions.updateInstallerError);
    const uploadPercent = useSelector((state: ApplicationState) => state.versions.uploadPercent);
    const uploadLoaded = useSelector((state: ApplicationState) => state.versions.uploadLoaded);
    const uploadTotal = useSelector((state: ApplicationState) => state.versions.uploadTotal);
    const fileInput = useRef<HTMLInputElement>(null);
    const handleAddFiles = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.target.files) {
            const file = event.target.files[0];
            if (file) {
                setInstallerData({ ...installerData, file: file });
            }
        }

        if (fileInput.current?.value) {
            fileInput.current.value = '';
        }
    };
    useEffect(() => {
        if (props.installer) {
            console.log(props.installer);
            console.log(mapTachoInstallerToAddTachoInstaller(props.installer));
            setInstallerData(mapTachoInstallerToAddTachoInstaller(props.installer));
        }
    }, [props.isOpen]);
    const isManagerPackage = installerData.releaseType === TachoReleaseType.ManagerPackage;
    return (
        <TachoModal
            size={'md'}
            height={'auto'}
            isOpen={props.isOpen}
            onToggle={() => {
                setInstallerData(defaultUpdateTachoInstallerModel);
                props.onToggle();
            }}
            onSubmit={onSubmit}
            title={<TachoEditModalTitle titleToTranslation="versionsUpdateVersion" icon={Icon.Edit} />}
            submitLabelToTranslate="commonEdit"
            submitIcon={Icon.Edit}
            isPending={updateInstallerPending}
            errorToTranslate={updateInstallerError}
            disableSubmitButton={
                !installerData.releaseDate.isValid ||
                installerData.versionMajor === undefined ||
                installerData.versionMinor === undefined ||
                installerData.versionPatch === undefined ||
                !installerData.patchNotes ||
                (!isManagerPackage && !!installerData.MD5 && !isMD5Valid(installerData.MD5)) ||
                (!isManagerPackage && !!installerData.SHA256 && !isSHA256Valid(installerData.SHA256))
            }
        >
            <Container fluid className="d-flex flex-column px-4 gap-3">
                <Label content={formatMessage({ id: 'versionsVersion' })} />
                <Row className="ms-3">
                    {isManagerPackage ? (
                        <InputRadio
                            justify={JustifyContents.flexStart}
                            labelPosition={LabelPosition.Right}
                            value={true}
                            onChange={() => undefined}
                            label={formatMessage({ id: TachoReleaseType.ManagerPackage })}
                            disabled
                        />
                    ) : (
                        <>
                            <InputRadio
                                justify={JustifyContents.flexStart}
                                labelPosition={LabelPosition.Right}
                                value={installerData.releaseType === TachoReleaseType.FullInstaller}
                                onChange={() =>
                                    setInstallerData({ ...installerData, releaseType: TachoReleaseType.FullInstaller })
                                }
                                label={formatMessage({ id: TachoReleaseType.FullInstaller })}
                                disabled={updateInstallerPending}
                            />

                            <InputRadio
                                justify={JustifyContents.flexStart}
                                labelPosition={LabelPosition.Right}
                                value={installerData.releaseType === TachoReleaseType.Updater}
                                onChange={() => setInstallerData({ ...installerData, releaseType: TachoReleaseType.Updater })}
                                label={formatMessage({ id: TachoReleaseType.Updater })}
                                disabled={updateInstallerPending}
                            />

                            <InputRadio
                                justify={JustifyContents.flexStart}
                                labelPosition={LabelPosition.Right}
                                value={installerData.releaseType === TachoReleaseType.UpdaterAsZip}
                                onChange={() =>
                                    setInstallerData({ ...installerData, releaseType: TachoReleaseType.UpdaterAsZip })
                                }
                                label={formatMessage({ id: TachoReleaseType.UpdaterAsZip })}
                                disabled={updateInstallerPending}
                            />
                        </>
                    )}
                </Row>
                <Row>
                    <Label
                        content={
                            installerData.file && installerData.file
                                ? installerData.file.name
                                : formatMessage({ id: 'versionsFileNotChoosen' })
                        }
                    />
                    {updateInstallerPending && uploadTotal > 0 && (
                        <span>
                            {formatMessage({ id: 'uploading' })} {Math.round(uploadLoaded / (1024 * 1024))} /{' '}
                            {Math.round(uploadTotal / (1024 * 1024))} MB - {uploadPercent}%
                        </span>
                    )}
                    {!updateInstallerPending && (
                        <Button
                            theme="primary"
                            text={formatMessage({ id: 'versionsAddFile' })}
                            onClick={() => fileInput.current?.click()}
                            icon={Icon.OppenFile}
                            className="chooseFileButton"
                        />
                    )}
                </Row>

                <Row>
                    <NumericInput
                        label={formatMessage({ id: 'versionsVersionMajor' })}
                        value={installerData?.versionMajor}
                        onChange={value => setInstallerData({ ...installerData, versionMajor: value })}
                        disabled={updateInstallerPending || isManagerPackage}
                    />
                </Row>
                <Row>
                    <NumericInput
                        label={formatMessage({ id: 'versionsVersionMinor' })}
                        value={installerData?.versionMinor}
                        onChange={value => setInstallerData({ ...installerData, versionMinor: value })}
                        disabled={updateInstallerPending || isManagerPackage}
                    />
                </Row>
                <Row>
                    <NumericInput
                        label={formatMessage({ id: 'versionsVersionPatch' })}
                        value={installerData?.versionPatch}
                        onChange={value => setInstallerData({ ...installerData, versionPatch: value })}
                        disabled={updateInstallerPending || isManagerPackage}
                    />
                </Row>
                <Row>
                    <DateInput
                        label={formatMessage({ id: 'versionsReleaseDate' })}
                        value={dateToStringFormat(installerData?.releaseDate)}
                        onChange={value => setInstallerData({ ...installerData, releaseDate: createDateFromTimeZone(value) })}
                        locale={config.language}
                        disabled={updateInstallerPending}
                    />
                </Row>
                <Row>
                    <TextArea
                        label={formatMessage({ id: 'versionsPatchNotes' })}
                        value={installerData?.patchNotes}
                        onChange={value => setInstallerData({ ...installerData, patchNotes: value as string })}
                        disabled={updateInstallerPending}
                    />
                </Row>
                {!isManagerPackage && (
                    <>
                        <Row>
                            <TextInput
                                label={formatMessage({ id: 'versionsMd5' })}
                                value={installerData.MD5}
                                onChange={value => setInstallerData({ ...installerData, MD5: value as string })}
                                disabled={updateInstallerPending}
                            />
                        </Row>
                        <Row>
                            <TextInput
                                label={formatMessage({ id: 'versionsSha256' })}
                                value={installerData.SHA256}
                                onChange={value => setInstallerData({ ...installerData, SHA256: value as string })}
                                disabled={updateInstallerPending}
                            />
                        </Row>
                        <Row>
                            <NumericInput
                                label={formatMessage({ id: 'size' })}
                                value={installerData.size}
                                onChange={value => setInstallerData({ ...installerData, size: +value })}
                                disabled={updateInstallerPending}
                            />
                        </Row>
                    </>
                )}
                <Row>
                    <ToogleSwitch
                        value={installerData.isBeta || false}
                        label={formatMessage({ id: 'versionsBeta' })}
                        disabled={updateInstallerPending}
                        onChange={value =>
                            setInstallerData({
                                ...installerData,
                                isBeta: value,
                            })
                        }
                    />
                </Row>
                {!isManagerPackage && (
                    <Row>
                        <TextInput
                            label={formatMessage({ id: 'versionsVariant' })}
                            value={installerData.variant ?? ''}
                            onChange={value => setInstallerData({ ...installerData, variant: value as string })}
                        />
                    </Row>
                )}
                <input
                    type="file"
                    className="d-none"
                    accept={
                        installerData.releaseType === TachoReleaseType.UpdaterAsZip ||
                        installerData.releaseType === TachoReleaseType.ManagerPackage
                            ? zippedExtension
                            : execExtension
                    }
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleAddFiles(event)}
                    ref={fileInput}
                />
            </Container>
        </TachoModal>
    );
};
